import { cn } from '@/src/utils/styles'
import { WalletCard } from '../connect-wallet/wallet-card'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog'
import { RepeatPulseRing } from '../ui/icons/repeat-pulse-ring'

interface ChangeAccountProps {
  className?: string
  leftWalletName?: string
  leftWalletIcon?: string
  rightWalletName: string
  rightWalletIcon?: string
  description?: string
  title?: string
  leftAccountName?: string
  rightAccountName?: string
  connectButtonOnClick?: () => void
}

const defaultTitle = 'Change wallet account'

export function ChangeAccount({
  className,
  leftWalletName,
  leftWalletIcon,
  rightWalletName,
  rightWalletIcon,
  title,
  description,
  leftAccountName,
  rightAccountName,
  connectButtonOnClick,
}: ChangeAccountProps) {
  const showLeft = !!leftWalletName
  const defaultDescription = showLeft
    ? `Open ${leftWalletName === rightWalletName ? leftWalletName : rightWalletName} and switch to another wallet account you wish to add.`
    : `Switch to the correct wallet account in your ${rightWalletName} browser extension to reconnect ${rightAccountName || 'Primary'}.`

  return (
    <div
      className={cn(
        'flex flex-col gap-8 items-center justify-center pt-10 pb-8',
        className,
      )}
    >
      <div className='flex flex-col gap-3 text-center leading-7'>
        <h2 className='text-4xl font-serif font-extralight text-primary'>
          {title || defaultTitle}
        </h2>
        <p className='text-secondary text-base'>
          {description || defaultDescription}
        </p>
      </div>
      <div
        className={cn(
          'flex items-center justify-center w-full',
          !showLeft && 'justify-center',
        )}
      >
        {showLeft ? (
          <>
            {/* Left wallet  */}
            {leftWalletName && (
              <WalletCard
                className='w-1/2 h-auto'
                isConnected
                isConnectable
                onConnect={() => {}}
                wallet={{
                  displayName: leftAccountName || leftWalletName,
                  logoUrl: leftWalletIcon,
                  id: leftWalletName,
                  website: leftWalletName,
                }}
                showAddAccountButton={false}
                showWalletBackground
                subtitle={<span className='text-secondary'>Connected</span>}
              />
            )}

            {/* Swap icon */}
            <div className='flex items-center justify-center'>
              <RepeatPulseRing size={48} />
            </div>
            {/* Right wallet */}
            <WalletCard
              className='w-1/2 h-auto'
              isConnected={false}
              isConnectable
              onConnect={() => {}}
              wallet={{
                displayName:
                  rightAccountName ?? rightWalletName ?? 'Your other account',
                id: rightWalletName,
                website: rightWalletName,
                logoUrl: rightWalletIcon,
              }}
              subtitle={<span className='text-secondary'>Disconnected</span>}
            />
          </>
        ) : (
          <WalletCard
            className='w-1/2 h-auto'
            isConnected={false}
            isConnectable
            onConnect={() => {}}
            wallet={{
              displayName:
                rightAccountName ?? rightWalletName ?? 'Your other account',
              id: rightWalletName,
              website: rightWalletName,
              logoUrl: rightWalletIcon,
            }}
            subtitle={<span className='text-secondary'>Disconnected</span>}
          />
        )}
      </div>
      <span className='text-tertiary text-sm text-center'>
        Switching accounts is different in every wallet. If your account change
        isn&apos;t detected, try refreshing the page and adding again.
      </span>
      {connectButtonOnClick && (
        <div className='flex flex-col justify-center w-full'>
          <div className='flex flex-row gap-3 items-center mb-12'>
            <span className='flex-1 h-px bg-border1' />
            <p className='text-sm leading-6 text-secondary'>OR</p>
            <span className='flex-1 h-px bg-border1' />
          </div>
          <Button
            className='w-full'
            variant='outline'
            onClick={connectButtonOnClick}
          >
            Connect a different wallet
          </Button>
        </div>
      )}
    </div>
  )
}

interface ChangeAccountDialogProps extends ChangeAccountProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function ChangeAccountDialog({
  isOpen,
  onOpenChange,
  className,
  leftWalletName,
  leftWalletIcon,
  rightWalletName,
  rightWalletIcon,
  description,
  leftAccountName,
  rightAccountName,
  connectButtonOnClick,
}: ChangeAccountDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby='change account dialog'
        className='sm:max-w-[28rem] w-full h-auto min-h-[unset] sm:min-w-[unset]'
        showCloseButton
        showBackButton={false}
        middle={<DialogTitle />}
      >
        <ChangeAccount
          className={cn(className, 'max-w-md p-8')}
          leftWalletName={leftWalletName}
          leftWalletIcon={leftWalletIcon}
          rightWalletName={rightWalletName}
          rightWalletIcon={rightWalletIcon}
          description={description}
          leftAccountName={leftAccountName}
          rightAccountName={rightAccountName}
          connectButtonOnClick={connectButtonOnClick}
        />
      </DialogContent>
    </Dialog>
  )
}
